import React, { useEffect, useState, useMemo } from "react";
import { Sprout, Plus, Search, Copy } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";

interface Crop {
  id: string;
  ranch_id: string | null;
  name: string;
  category: "vegetable" | "fruit" | "grain" | "forage" | "herb" | "flower";
  growing_season: "cool" | "warm" | "year_round";
  is_system: number;
  description: string | null;
}

const CATEGORY_COLORS: Record<Crop["category"], string> = {
  vegetable: "bg-moss/15 text-moss border-moss/30 dark:bg-moss-dark/20 dark:text-moss-dark dark:border-moss-dark/30",
  fruit:     "bg-clay/15 text-clay border-clay/30 dark:bg-clay-dark/20 dark:text-clay-dark dark:border-clay-dark/30",
  grain:     "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30",
  forage:    "bg-olive/15 text-olive border-olive/30 dark:bg-olive-dark/20 dark:text-olive-dark dark:border-olive-dark/30",
  herb:      "bg-sky-muted/15 text-sky-muted border-sky-muted/30 dark:bg-sky-muted-dark/20 dark:text-sky-muted-dark dark:border-sky-muted-dark/30",
  flower:    "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30",
};

const SEASON_LABELS: Record<Crop["growing_season"], string> = {
  cool: "Cool season",
  warm: "Warm season",
  year_round: "Year round",
};

export function CropsPage() {
  const [crops, setCrops] = useState<Crop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState<"all" | "ranch" | "system">("all");
  const [cloningId, setCloningId] = useState<string | null>(null);

  const fetchCrops = async () => {
    try {
      const data = await authFetch<{ items: Crop[] }>("/api/crops");
      setCrops(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load crops";
      setError(msg);
      console.error("[Crops]", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCrops();
  }, []);

  const filtered = useMemo(() => {
    return crops.filter((c) => {
      const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
      const matchCategory = categoryFilter === "all" || c.category === categoryFilter;
      const matchScope =
        scopeFilter === "all"
          ? true
          : scopeFilter === "system"
          ? c.is_system === 1
          : c.is_system === 0;
      return matchSearch && matchCategory && matchScope;
    });
  }, [crops, search, categoryFilter, scopeFilter]);

  const handleClone = async (cropId: string, name: string) => {
    setCloningId(cropId);
    try {
      await authFetch(`/api/crops/${cropId}/clone`, {
        method: "POST",
        body: JSON.stringify({ name: `${name} (mine)` }),
      });
      toast.success(`${name} cloned to your library`);
      setLoading(true);
      fetchCrops();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Clone failed";
      toast.error(msg);
    } finally {
      setCloningId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Crops"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Crops" }]}
      >
        <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
          <Plus className="h-4 w-4 mr-2" /> Add Crop
        </Button>
      </PageHeader>

      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search crops..."
            className="pl-9"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            <SelectItem value="vegetable">Vegetable</SelectItem>
            <SelectItem value="fruit">Fruit</SelectItem>
            <SelectItem value="grain">Grain</SelectItem>
            <SelectItem value="forage">Forage</SelectItem>
            <SelectItem value="herb">Herb</SelectItem>
            <SelectItem value="flower">Flower</SelectItem>
          </SelectContent>
        </Select>
        <Select value={scopeFilter} onValueChange={(v) => setScopeFilter(v as typeof scopeFilter)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All crops</SelectItem>
            <SelectItem value="ranch">My crops only</SelectItem>
            <SelectItem value="system">System library</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <CardGridSkeleton count={6} />
      ) : error ? (
        <Card className="p-6 text-center text-clay dark:text-clay-dark">{error}</Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sprout}
          title="No crops"
          description="Try adjusting your filters or clone a system crop to get started."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <Card key={c.id} className="p-5 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sprout className="h-5 w-5 text-ranch-green" />
                  <h3 className="font-semibold text-foreground">{c.name}</h3>
                </div>
                {c.is_system === 1 && (
                  <Badge variant="outline" className="bg-moss/10 text-moss border-moss/30 dark:bg-moss-dark/15 dark:text-moss-dark dark:border-moss-dark/30">
                    System
                  </Badge>
                )}
              </div>
              <div className="flex gap-2 mb-2">
                <Badge variant="outline" className={CATEGORY_COLORS[c.category]}>
                  {c.category}
                </Badge>
                <span className="text-xs text-muted-foreground self-center">
                  {SEASON_LABELS[c.growing_season]}
                </span>
              </div>
              {c.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {c.description}
                </p>
              )}
              {c.is_system === 1 ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  disabled={cloningId === c.id}
                  onClick={() => handleClone(c.id, c.name)}
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" />
                  {cloningId === c.id ? "Cloning..." : "Clone to my library"}
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground italic">Ranch crop</p>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
